/**
 * Sprint M5.39 (30.07.2026) — Block B2.
 *
 * Multi-Kurs-Video-Bibliothek — FunnelFlix-Stil Kachel-Grid.
 *
 * Sprint M5.42 Block B (31.07.2026):
 * Wenn `isPionier=true`, wird zusaetzlich der Vorab-Einblick-exklusive
 * Kurs „Ruhe im Chaos" prominent oben angezeigt.
 *
 * Sprint M5.44 (31.07.2026) — Free-Cockpit-UI-Drift:
 * - Neuer Prop `showComingSoon` (default true). Free-Plan setzt false,
 *   dann werden Stressmanagement + Fokustypen-Radar entfernt.
 * - Live-Badge → „online" (Free-Sprech).
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Clock, PlayCircle, Sparkles } from "lucide-react";

const COURSE_FOKUSTYPEN_GRUNDLAGEN = {
  id: "fokustypen-grundlagen",
  title: "Fokustypen — die Grundlagen",
  subtitle: "5 kurze Videos · dein Einstieg ins Vier-Fokustypen-Modell",
  videoCount: 5,
  duration: "~7 Min",
  status: "live",
  href: "/dashboard/videos",
  accent: "bg-[#4563BB] text-white",
};

const COURSES_COMING_SOON = [
  {
    id: "stressmanagement",
    title: "Stressmanagement",
    subtitle: "Selbstkontrolle & Impulskontrolle — Voraussetzung für bewusste Kommunikation.",
    duration: "Q4 2026",
    status: "coming_soon",
    accent: "bg-slate-100 text-slate-500",
  },
  {
    id: "fokustypen-radar",
    title: "Fokustypen-Radar",
    subtitle: "Fokustypen in Echtzeit erkennen — im Gespräch, in E-Mails, im Team.",
    duration: "Q4 2026",
    status: "coming_soon",
    accent: "bg-slate-100 text-slate-500",
  },
];

const PIONIER_COURSE = {
  id: "ruhe-im-chaos",
  title: "Ruhe im Chaos",
  // Sprint V11 (Boardroom Sa 05.09.2026-17 Uhr) — Cut 5: "Pionier-Bonus"
  // Sublabels entfernt (kein Pionier-Onboarding im Launch V1).
  subtitle: "4 Impulse für mehr Boden unter den Füßen.",
  videoCount: 4,
  duration: "4 Videos",
  status: "live",
  href: "/dashboard/courses/ruhe-im-chaos",
  accent: "bg-amber-500 text-white",
  isPionier: true,
};

export default function MultiKursBibliothek({ onOpenCourse, isPionier = false, showComingSoon = true }) {
  const navigate = useNavigate();
  const base = showComingSoon
    ? [COURSE_FOKUSTYPEN_GRUNDLAGEN, ...COURSES_COMING_SOON]
    : [COURSE_FOKUSTYPEN_GRUNDLAGEN];
  const courses = isPionier ? [PIONIER_COURSE, ...base] : base;

  const handleOpen = (c) => {
    if (c.status !== "live") return;
    if (c.href) {
      navigate(c.href);
      return;
    }
    onOpenCourse?.(c);
  };

  return (
    <section
      data-testid="multi-kurs-bibliothek"
      className="mt-8"
    >
      <div className="mb-5">
        <p className="text-xs font-bold uppercase tracking-widest text-[#4563BB]">
          Deine Kurs-Bibliothek
        </p>
        <h2 className="mt-1 text-lg sm:text-xl font-extrabold text-slate-900">
          Videos & Kurse zum Vier-Fokustypen-Modell
        </h2>
      </div>

      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        data-testid="multi-kurs-grid"
      >
        {courses.map((c) => (
          <article
            key={c.id}
            data-testid={`kurs-card-${c.id}`}
            data-status={c.status}
            className={`relative rounded-2xl border p-5 flex flex-col ${
              c.status === "live"
                ? c.isPionier
                  ? "border-amber-300 bg-amber-50 hover:border-amber-500 hover:shadow-md transition-all cursor-pointer"
                  : "border-slate-200 bg-white hover:border-[#4563BB] hover:shadow-md transition-all cursor-pointer"
                : "border-slate-200 bg-slate-50"
            }`}
            onClick={() => handleOpen(c)}
            role={c.status === "live" ? "button" : undefined}
            tabIndex={c.status === "live" ? 0 : undefined}
            onKeyDown={(e) => {
              if (c.status === "live" && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                handleOpen(c);
              }
            }}
          >
            <div className="flex items-start justify-between gap-2">
              <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${c.accent}`}>
                {c.isPionier ? (
                  <>
                    <Sparkles className="w-3 h-3" />
                    Bonus
                  </>
                ) : c.status === "live" ? (
                  <>
                    <PlayCircle className="w-3 h-3" />
                    Online
                  </>
                ) : (
                  <>
                    <Clock className="w-3 h-3" />
                    Bald verfügbar
                  </>
                )}
              </div>
              {c.status === "live" && c.videoCount && (
                <span className="text-[11px] font-semibold text-slate-500">
                  {c.videoCount} Videos · {c.duration}
                </span>
              )}
              {c.status !== "live" && (
                <span className="text-[11px] font-semibold text-slate-500">{c.duration}</span>
              )}
            </div>

            <h3 className="mt-3 text-base font-extrabold text-slate-900 leading-tight">
              {c.title}
            </h3>
            <p className="mt-1.5 text-sm text-slate-600 leading-snug flex-1">
              {c.subtitle}
            </p>

            {c.status === "live" && (
              <div className={`mt-4 inline-flex items-center gap-1 text-xs font-semibold ${c.isPionier ? "text-amber-700" : "text-[#4563BB]"}`}>
                Kurs öffnen <ArrowRight className="w-3 h-3" />
              </div>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}


